import React, { useState } from 'react';
import { supabase } from '../config/supabase';

const HeroSection: React.FC = () => {
  const [phone, setPhone] = useState('');
  const [bestTime, setBestTime] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleCallbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!phone.trim()) {
      setShowError(true);
      return;
    }

    setIsSubmitting(true);
    setShowError(false);

    try {
      // Clean phone number
      const cleanPhone = phone.replace(/\D/g, '');

      const insertData = {
        phone: cleanPhone,
        best_time: bestTime || 'anytime',
        source: 'hero_section'
      };

      const { error } = await supabase
        .from('quick_callbacks')
        .insert([insertData]);

      if (error) {
        console.error('Supabase error:', error);
        setShowError(true);
      } else {
        setShowSuccess(true);
        setPhone('');
        setBestTime('');

        // Google Analytics tracking
        if (typeof (window as any).gtag !== 'undefined') {
          (window as any).gtag('event', 'contact_info_submitted', {
            event_category: 'lead_generation',
            event_label: 'hero_callback',
            custom_parameters: {
              source: 'hero_section',
              best_time: bestTime || 'anytime'
            }
          });

          // Google Ads conversion tracking
          (window as any).gtag('event', 'conversion', {
            'send_to': 'AW-17868365802/pBSECPKF4OobEOq_pshC',
            'value': 500.0,
            'currency': 'CAD'
          });
        }

        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Error submitting callback request:', error);
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="relative py-12 md:py-20 min-h-screen flex items-center"
      style={{
        background: "linear-gradient(135deg, rgba(26, 26, 26, 0.7), rgba(26, 26, 26, 0.5)), url('images/pergolas/pic1.webp') center/cover"
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center relative z-10 w-full">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Premium Outdoor Living Solutions
          </h1>

          {/* Service Area Announcement */}
          <div className="bg-accent text-white px-6 py-3 rounded-full mb-6 inline-block shadow-lg">
            <p className="text-lg font-semibold">🍁 Serving Toronto & Southern Ontario</p>
          </div>

          <p className="text-xl md:text-2xl text-white mb-4">Built for Canadian Living</p>

          {/* Summer Installation Guarantee Badge */}
          <div className="mb-8 animate-bounce delay-1000 inline-block">
            <div className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-lg shadow-xl border-2 border-yellow-200 transform -rotate-1 hover:rotate-0 transition-transform cursor-default">
              <span className="text-2xl mr-2">☀️</span>
              <span className="font-bold">Summer Installation Guarantee:</span>
              <span className="block text-sm font-medium">Order by March 31st to guarantee July installation</span>
            </div>
          </div>

          <p className="text-lg text-white/90 mb-8 max-w-3xl mx-auto">
            Transform your outdoor space with luxury pergolas and motorized awnings.
            Professionally installed across Ontario with industry-leading warranties.
          </p>

          {/* Quick Callback Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-8 max-w-md mx-auto mb-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-primary mb-4">Get a Quick Callback</h3>
            <p className="text-gray-600 mb-6">We'll call you within 24 hours to discuss your project</p>

            <form onSubmit={handleCallbackSubmit} className="space-y-4">
              <div>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Your phone number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  required
                />
              </div>

              <div>
                <select
                  value={bestTime}
                  onChange={(e) => setBestTime(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                >
                  <option value="">Best time to call (optional)</option>
                  <option value="morning">Morning (9 AM - 12 PM)</option>
                  <option value="afternoon">Afternoon (12 PM - 5 PM)</option>
                  <option value="evening">Evening (5 PM - 8 PM)</option>
                  <option value="anytime">Anytime</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : '📞 Request Callback'}
              </button>
            </form>

            {/* Success Message */}
            {showSuccess && (
              <div className="mt-3 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                <p>✅ Thank you! We'll call you within 24 hours.</p>
              </div>
            )}

            {/* Error Message */}
            {showError && (
              <div className="mt-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <p>❌ Something went wrong. Please try again.</p>
              </div>
            )}
          </div>

          {/* Additional CTA Option */}
          <div className="mt-8 text-center">
            <p className="text-white/90 mb-4">Or get your instant quote in under 1 minute:</p>
            <a
              href="/pergola-quote"
              className="bg-white text-primary px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block shadow-lg"
            >
              ⚡ Get Instant Pergola Quote (Under 1 Min)
            </a>
            <p className="text-white/80 text-sm mt-3 font-medium">✓ No sales call required to see pricing</p>
          </div>

          {/* Trust Indicators */}
          <div className="mt-12 flex flex-col items-center">
            <div className="flex items-center gap-2 text-white mb-2">
              <span className="text-2xl">⭐</span>
              <span className="font-semibold">Proudly Serving All of Ontario • Built for Canadian Winters</span>
            </div>
            <div className="flex items-center gap-1 text-yellow-400">
              <span>★★★★★</span>
              <span className="text-white ml-2">4.9/5 from 47 homeowners</span>
            </div>
          </div>
        </div>
      </div>
    </section >
  );
};

export default HeroSection;
